import { ApiRequest } from '../baseService'

const BaseRoute = 'app/assistant'

/**
 * Where a conversation was started from, stored on it when it is created: a placement's key (one
 * of the keys the server knows, e.g. 'case-history') and the record the placement sits on. A key
 * only, never a list of tools; the server ignores a key it does not know.
 */
export interface CreateAssistantConversationBody {
  placement?: string
  subjectType?: string
  subjectId?: string
}

/**
 * `signal` lets the caller abandon the request (Stop pressed before the first turn started).
 * Without `body` (the drawer) nothing is sent with the request, exactly as before placements.
 */
function CreateConversation(
  signal?: AbortSignal,
  body?: CreateAssistantConversationBody
) {
  return ApiRequest.post(`${BaseRoute}/conversations`, body, { signal })
}

/**
 * The newest of the user's conversations that was started from `placement` about the record
 * `subjectType` / `subjectId`, holds a message and whose last question was asked from that
 * placement (one handed off to the drawer and continued there is not). The answer is the
 * conversation, or an empty body when there is none. Silent: a placement that finds nothing to
 * go on with simply starts from an empty chat.
 */
function GetLatestConversation(params: {
  placement: string
  subjectType: string
  subjectId: string
}) {
  return ApiRequest.get(`${BaseRoute}/conversations/latest`, {
    params,
    silent: true,
  })
}

/**
 * One page of the user's conversations, newest first; only conversations that hold a message.
 * `filter` keeps those whose title contains the text. `beforeLastMessageAt` asks for the page
 * after the ones already loaded: the `lastMessageAt` of the last of them, exactly as received.
 * The answer carries `items`, `totalCount` (every match, not only this page) and `hasMore`.
 */
function GetConversations(params?: {
  maxResultCount?: number
  includeArchived?: boolean
  filter?: string
  beforeLastMessageAt?: string
  /** Only the conversations started from this placement (its key, e.g. 'case-history'). */
  placement?: string
  /**
   * With `placement` and `subjectId`: only that placement's conversations about this record
   * (e.g. 'case'), the list a placement shows of its own. The server ignores a record sent
   * without a placement.
   */
  subjectType?: string
  subjectId?: string
}) {
  return ApiRequest.get(`${BaseRoute}/conversations`, {
    params: { maxResultCount: 50, ...params },
  })
}

/**
 * The conversation with its newest messages, or, with `before` (the `earlierBefore` of the page
 * loaded last, exactly as received), the page of messages before that one. The answer says with
 * `hasEarlier` whether older messages exist.
 * `silent`: a read made in the background (the check of a turn that was cut off). Its failure is
 * the caller's to handle; no error notification is shown for it.
 */
function GetConversation(
  id: string,
  config?: { silent?: boolean; before?: string }
) {
  if (!config) {
    return ApiRequest.get(`${BaseRoute}/conversations/${id}`)
  }
  const { before, ...rest } = config
  return ApiRequest.get(
    `${BaseRoute}/conversations/${id}`,
    before ? { ...rest, params: { before } } : rest
  )
}

/** The user has read the privacy notice of this version: it is not shown to them again. */
function AcknowledgePrivacyNotice(version: string) {
  return ApiRequest.post(
    `${BaseRoute}/privacy-notice/acknowledge`,
    { version },
    { silent: true }
  )
}

function DeleteConversation(id: string) {
  return ApiRequest.delete(`${BaseRoute}/conversations/${id}`)
}

/**
 * Names the user's own conversation (plain text, at most 60 characters). The answer is the
 * conversation with the title as it was stored; a name given this way is never replaced by a
 * generated title. Silent: the history row says itself when the name could not be saved.
 */
function RenameConversation(id: string, title: string) {
  return ApiRequest.put(
    `${BaseRoute}/conversations/${id}/title`,
    { title },
    { silent: true }
  )
}

/**
 * Rates an answer: 1 (good), -1 (bad) or 0, which takes the rating back. `reason` is one of the
 * server's codes and goes with a bad rating; `comment` is an optional note. Sent again with the
 * same rating, a reason or a note that is left out stays as it was.
 * Silent: the rating buttons say themselves when a rating could not be saved.
 */
function SubmitFeedback(
  messageId: string,
  data: { rating: 1 | -1 | 0; reason?: string; comment?: string }
) {
  return ApiRequest.post(`${BaseRoute}/messages/${messageId}/feedback`, data, {
    silent: true,
  })
}

/** Confirms an action the assistant proposed — the only way it runs. */
function ConfirmAction(actionId: string) {
  return ApiRequest.post(`${BaseRoute}/actions/${actionId}/confirm`)
}

function CancelAction(actionId: string) {
  return ApiRequest.post(`${BaseRoute}/actions/${actionId}/cancel`)
}

/** Today's briefing for the current user (deterministic; cached briefly server-side). */
function GetBriefing(refresh = false) {
  return ApiRequest.get(`${BaseRoute}/briefing`, { params: { refresh } })
}

/** The user-guide pages the current user may read (no text). */
function GetDocs() {
  return ApiRequest.get(`${BaseRoute}/docs`)
}

/** One user-guide page (markdown). */
function GetDoc(key: string) {
  return ApiRequest.get(`${BaseRoute}/docs/${encodeURIComponent(key)}`)
}

/** `silent`: a re-check made in the background; see GetConversation. */
function GetCapabilities(config?: { silent?: boolean }) {
  return ApiRequest.get(`${BaseRoute}/capabilities`, config)
}

/**
 * @-mention typeahead. `signal` abandons the request when the next keystroke replaces it, which
 * also stops its queries on the server.
 */
function SearchMentions(
  params: {
    type?: string
    query: string
    top?: number
  },
  signal?: AbortSignal
) {
  return ApiRequest.get(`${BaseRoute}/mentions`, { params, signal })
}

/**
 * Voice input: the recording (one file, at most 10 MB) is transcribed by the server, and the
 * answer is the text heard (`text`, '' when no speech was recognised) and the length of the
 * audio (`durationMs`). Neither the audio nor the text is kept. `signal` abandons the request
 * when the user cancels. Silent: the composer says itself what went wrong.
 */
function Transcribe(
  audio: Blob,
  fileName: string,
  signal?: AbortSignal
): Promise<{ data: { text: string; durationMs: number } }> {
  const formData = new FormData()
  formData.append('audio', audio, fileName)
  return ApiRequest.post(`${BaseRoute}/transcribe`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    signal,
    silent: true,
  })
}

const API = {
  AcknowledgePrivacyNotice,
  CreateConversation,
  GetConversations,
  GetLatestConversation,
  GetConversation,
  DeleteConversation,
  RenameConversation,
  SubmitFeedback,
  ConfirmAction,
  CancelAction,
  GetBriefing,
  GetCapabilities,
  GetDoc,
  GetDocs,
  SearchMentions,
  Transcribe,
}

export default API
